import { jsPDF } from "jspdf";
import Papa from "papaparse";

import { dayjs } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type { AuditTrailEntryUnbranded } from "@thalamos/vision-common";

export interface ActivityLogRow {
  timestamp: string;
  user: string;
  organisation: string;
  message: string;
}

function formatTimestamp(date: Date | string): string {
  return dayjs.utc(date).tz("Europe/London").format("DD/MM/YYYY HH:mm:ss");
}

export function extractActivityLogRows(
  entries: AuditTrailEntryUnbranded[],
  getMessage: (entry: AuditTrailEntryUnbranded) => string,
): ActivityLogRow[] {
  const sorted = [...entries].sort((a, b) =>
    dayjs(a.timestamp).isBefore(dayjs(b.timestamp)) ? 1 : -1,
  );

  return sorted.map((entry) => ({
    timestamp: formatTimestamp(entry.timestamp),
    user: entry.userName ?? "",
    organisation: entry.organisationName ?? "",
    message: getMessage(entry),
  }));
}

export async function copyActivityLogToClipboard(
  rows: ActivityLogRow[],
): Promise<boolean> {
  const lines = rows.map((r) => {
    const who = [r.user, r.organisation].filter(Boolean).join(" — ");
    return `[${r.timestamp}] ${who ? `${who}: ` : ""}${r.message}`;
  });

  const text = lines.join("\n\n");

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function downloadActivityLogCsv(
  rows: ActivityLogRow[],
  title: string,
): void {
  const fields = ["Timestamp", "User", "Organisation", "Message"];
  const data = rows.map((r) => [
    r.timestamp,
    r.user,
    r.organisation,
    r.message,
  ]);

  const csv = Papa.unparse({ fields, data });
  const dataUri = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  triggerDownload(dataUri, `${title}.csv`);
}

export function downloadActivityLogPdf(
  rows: ActivityLogRow[],
  title: string,
): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const usableWidth = pageWidth - margin * 2;
  let y = margin;

  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  const titleLines = doc.splitTextToSize(title, usableWidth) as string[];
  doc.text(titleLines, margin, y);
  y += titleLines.length * 7 + 4;

  // Entries
  doc.setFontSize(9);
  const lineHeight = 4;
  const entryGap = 5;
  const maxY = pageHeight - margin;

  function writeLine(text: string, font: "bold" | "normal") {
    if (y + lineHeight > maxY) {
      doc.addPage();
      y = margin;
    }
    doc.setFont("helvetica", font);
    const wrapped = doc.splitTextToSize(text, usableWidth) as string[];
    for (const line of wrapped) {
      if (y + lineHeight > maxY) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }
  }

  for (const row of rows) {
    const who = [row.user, row.organisation].filter(Boolean).join(" — ");
    writeLine(`${row.timestamp}${who ? ` | ${who}` : ""}`, "bold");
    writeLine(row.message, "normal");
    y += entryGap;
  }

  doc.save(`${title}.pdf`);
}
